import type {SessionState} from '@github-ui/agent-sessions/types/session'

import type {AgentTask} from '../../api/memex-items/contracts'
import type {MemexItemModel} from '../../models/memex-item-model'

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000

function isOlderThan24Hours(timestamp: string): boolean {
  const age = Date.now() - new Date(timestamp).getTime()
  return age > TWENTY_FOUR_HOURS_MS
}

/**
 * Returns false if:
 * - No state (item has no agent task)
 * - lastUpdatedAt is set and older than 24 hours
 */
export function shouldShowAgentTaskPill(state: SessionState | undefined, lastUpdatedAt: string | undefined): boolean {
  if (!state) return false
  if (lastUpdatedAt && isOlderThan24Hours(lastUpdatedAt)) return false
  return true
}

/** Maximum number of agent task sub-rows to display per issue. */
export const MAX_AGENT_TASK_SUB_ROWS = 5

/**
 * Returns whether a given agent task entry should be visible as a sub-row.
 */
function isVisibleTask(task: AgentTask): boolean {
  return shouldShowAgentTaskPill(task.state, task.lastUpdatedAt)
}

/**
 * Returns true if the task is "in progress" (not yet completed/failed/cancelled/timed_out).
 * These tasks have priority when capping the visible list.
 */
export function isProgressTask(task: AgentTask): boolean {
  return (
    task.state === 'in_progress' ||
    task.state === 'queued' ||
    task.state === 'waiting_for_user' ||
    task.state === 'idle'
  )
}

/**
 * Returns up to MAX_AGENT_TASK_SUB_ROWS visible agent tasks for an item, prioritizing
 * in-progress tasks over completed/failed/cancelled ones when more than the max exist.
 */
export function getVisibleAgentTasks(model: MemexItemModel): Array<AgentTask> {
  const tasks = model.agentTasks ?? []

  const visible = tasks.filter(isVisibleTask)

  if (visible.length <= MAX_AGENT_TASK_SUB_ROWS) {
    return visible
  }

  // More than max: prioritize in-progress tasks over completed ones
  const inProgress = visible.filter(isProgressTask)
  const completed = visible.filter(t => !isProgressTask(t))

  return [...inProgress, ...completed].slice(0, MAX_AGENT_TASK_SUB_ROWS)
}

/**
 * Returns the state of the most recently created agent task
 */
export function getLatestAgentTaskState(tasks: Array<AgentTask> | undefined): SessionState | null {
  if (!tasks || tasks.length === 0) return null

  const latest = tasks.reduce((a, b) => (new Date(b.createdAt ?? 0) > new Date(a.createdAt ?? 0) ? b : a))
  return latest.state ?? null
}
